<template>
  <div class="draw-tool-wrapper">
    <DrawDialog
      v-model="drawDialogInput"
      :title="drawDialog.title"
      :message="drawDialog.message"
      :tone="drawDialog.tone"
      :input-visible="drawDialog.inputVisible"
      :input-label="drawDialog.inputLabel"
      :placeholder="drawDialog.placeholder"
      :error="drawDialogError"
      :show-cancel="drawDialog.showCancel"
      :confirm-label="drawDialog.confirmLabel"
      :cancel-label="drawDialog.cancelLabel"
      @confirm="confirmDrawDialog"
      @cancel="cancelDrawDialog"
      @closed="onDrawDialogClosed"
    />

    <div
      v-show="isActive"
      id="drawID"
      class="map-tool-window draw-tool-window"
    >
      <!-- Header -->
      <div
        id="drawIDheader"
        class="map-tool-window-header"
      >
        <p class="map-tool-window-header-title">
          Dibujar
        </p>

        <div class="map-tool-window-header-actions">
          <button
            type="button"
            class="control-btn"
            @click="isHidden = !isHidden"
          >
            <svg
              v-if="isHidden"
              viewBox="0 0 24 24"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>

            <svg
              v-else
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>

          <button
            type="button"
            class="control-btn"
            @click="exitDraw()"
          >
            <svg viewBox="0 0 24 24">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div
        v-show="!isHidden"
        class="map-tool-window-body draw-tool-body"
      >
        <!-- Herramienta activa -->
        <div
          v-if="activeMode"
          class="draw-current-mode"
        >
          <span>Modo activo</span>

          <strong>
            {{ activeModeLabel }}
          </strong>
        </div>

        <!-- Herramientas de dibujo -->
        <section class="draw-section">
          <p class="draw-section-title">
            Herramientas
          </p>

          <div class="draw-buttons-grid">
            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Punto"
              :aria-pressed="isDrawingTypeActive('Point')"
              :class="{ 'active-tool': isDrawingTypeActive('Point') }"
              @click="initDraw('Point')"
            >
              <font-awesome-icon icon="map-marker-alt" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Línea"
              :aria-pressed="isDrawingTypeActive('LineString')"
              :class="{ 'active-tool': isDrawingTypeActive('LineString') }"
              @click="initDraw('LineString')"
            >
              <font-awesome-icon icon="minus" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Polígono"
              :aria-pressed="isDrawingTypeActive('Polygon')"
              :class="{ 'active-tool': isDrawingTypeActive('Polygon') }"
              @click="initDraw('Polygon')"
            >
              <font-awesome-icon icon="draw-polygon" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Círculo"
              :aria-pressed="isDrawingTypeActive('Circle')"
              :class="{ 'active-tool': isDrawingTypeActive('Circle') }"
              @click="initDraw('Circle')"
            >
              <font-awesome-icon icon="circle" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Flecha"
              :aria-pressed="isDrawingTypeActive('Arrow')"
              :class="{ 'active-tool': isDrawingTypeActive('Arrow') }"
              @click="initDraw('Arrow')"
            >
              <font-awesome-icon icon="arrow-up" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujar Elipse"
              :aria-pressed="isDrawingTypeActive('Ellipse')"
              :class="{ 'active-tool': isDrawingTypeActive('Ellipse') }"
              @click="initDraw('Ellipse')"
            >
              <span
                class="draw-ellipse-icon"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Dibujo Mano Alzada"
              :aria-pressed="isDrawingTypeActive('Freehand')"
              :class="{ 'active-tool': isDrawingTypeActive('Freehand') }"
              @click="initDraw('Freehand')"
            >
              <font-awesome-icon icon="signature" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Texto"
              :aria-pressed="isDrawingTypeActive('Text')"
              :class="{ 'active-tool': isDrawingTypeActive('Text') }"
              @click="initDraw('Text')"
            >
              <font-awesome-icon icon="font" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Seleccionar un dibujo para editar su estilo"
              :aria-pressed="activeMode === 'select'"
              :class="{ 'active-tool': activeMode === 'select' }"
              @click="activateSelector()"
            >
              <font-awesome-icon icon="mouse-pointer" />
            </button>

            <button
              type="button"
              class="draw-tool-btn"
              title="Goma: borrar una figura"
              :aria-pressed="activeMode === 'erase'"
              :class="{ 'active-tool': activeMode === 'erase' }"
              @click="activateEraser()"
            >
              <font-awesome-icon icon="eraser" />
            </button>
          </div>

          <!-- Deshacer, rehacer y borrar -->
          <div class="draw-actions-row">
            <button
              type="button"
              class="draw-history-btn"
              title="Deshacer"
              :disabled="!canUndo"
              @click="undoAction()"
            >
              <font-awesome-icon icon="undo" />
            </button>

            <button
              type="button"
              class="draw-history-btn"
              title="Rehacer"
              :disabled="!canRedo"
              @click="redoAction()"
            >
              <font-awesome-icon icon="redo" />
            </button>

            <button
              type="button"
              class="draw-clear-btn"
              title="Borrar todo"
              @click="requestClearAllDrawings()"
            >
              <font-awesome-icon icon="trash-alt" />
              Borrar todo
            </button>
          </div>
        </section>

        <div
          v-if="lastEditableFeature"
          class="draw-editing-status"
        >
          <font-awesome-icon icon="pen" />

          <span>
            Editando última figura:
            <strong>{{ toolNames[lastEditableDrawingType] }}</strong>
          </span>
        </div>

        <div
          v-if="selectedFeature"
          class="draw-editing-status draw-editing-status--selected"
        >
          <font-awesome-icon icon="mouse-pointer" />

          <span>
            Editando figura seleccionada:
            <strong>{{ toolNames[selectedDrawingType] }}</strong>
          </span>
        </div>

        <section
          v-if="shapeMeasurementItems.length"
          class="draw-measurement-card"
          aria-live="polite"
        >
          <div class="draw-measurement-card__header">
            <font-awesome-icon icon="ruler-combined" />

            <div>
              <strong>Medidas aproximadas</strong>
              <span>{{ shapeMeasurementContext }}</span>
            </div>
          </div>

          <dl class="draw-measurement-list">
            <div
              v-for="item in shapeMeasurementItems"
              :key="item.label"
            >
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>

          <small>
            Calculadas sobre la superficie terrestre.
          </small>
        </section>

        <!-- Configuración -->
        <section
          v-if="hasStyleControls"
          class="interactionConfig draw-section"
        >
          <p class="draw-section-title">
            Configuración de estilo
          </p>

          <label v-if="styleControls.lineStyle">
            <span>Estilo de línea</span>

            <select
              v-model="drawStyleConfig.lineStyle"
              class="draw-select"
              @change="debouncedUpdate"
            >
              <option value="Sólida">Sólida</option>
              <option value="Segmentada">Segmentada</option>
              <option value="Punteada">Punteada</option>
            </select>
          </label>

          <label v-if="styleControls.outlineStyle">
            <span>Estilo de contorno</span>

            <select
              v-model="drawStyleConfig.outlineStyle"
              class="draw-select"
              @change="debouncedUpdate"
            >
              <option value="Sólida">Sólido</option>
              <option value="Segmentada">Segmentado</option>
              <option value="Punteada">Punteado</option>
            </select>
          </label>

          <label v-if="styleControls.line">
            <span>Color de línea</span>

            <input
              v-model="drawStyleConfig.lineColor"
              type="color"
              class="draw-color-input"
              @input="debouncedUpdate"
            >
          </label>

          <label v-if="styleControls.line">
            <span>
              Grosor de línea ({{ drawStyleConfig.lineWidth }})
            </span>

            <input
              v-model="drawStyleConfig.lineWidth"
              type="range"
              min="1"
              max="10"
              class="draw-range"
              @input="debouncedUpdate"
            >
          </label>

          <template v-if="styleControls.fill">
            <label>
              <span>Color de relleno</span>

              <input
                v-model="drawStyleConfig.fillColor"
                type="color"
                class="draw-color-input"
                @input="debouncedUpdate"
              >
            </label>

            <label>
              <span>
                Opacidad de relleno
                ({{ Math.round(drawStyleConfig.fillOpacity * 100) }}%)
              </span>

              <input
                v-model="drawStyleConfig.fillOpacity"
                type="range"
                min="0"
                max="1"
                step="0.1"
                class="draw-range"
                @input="debouncedUpdate"
              >
            </label>
          </template>

          <template v-if="styleControls.outline">
            <label>
              <span>Color de contorno</span>

              <input
                v-model="drawStyleConfig.outlineColor"
                type="color"
                class="draw-color-input"
                @input="debouncedUpdate"
              >
            </label>

            <label>
              <span>
                Grosor de contorno ({{ drawStyleConfig.outlineWidth }})
              </span>

              <input
                v-model="drawStyleConfig.outlineWidth"
                type="range"
                min="0"
                max="10"
                class="draw-range"
                @input="debouncedUpdate"
              >
            </label>
          </template>

          <label v-if="styleControls.text">
            <span>Color de texto</span>

            <input
              v-model="drawStyleConfig.textColor"
              type="color"
              class="draw-color-input"
              @input="debouncedUpdate"
            >
          </label>

          <label v-if="styleControls.text">
            <span>
              Tamaño de fuente ({{ drawStyleConfig.fontSize }} px)
            </span>

            <input
              v-model="drawStyleConfig.fontSize"
              type="range"
              min="10"
              max="48"
              class="draw-range"
              @input="debouncedUpdate"
            >
          </label>

          <label v-if="styleControls.pointRadius">
            <span>
              Tamaño de punto ({{ drawStyleConfig.pointRadius }})
            </span>

            <input
              v-model="drawStyleConfig.pointRadius"
              type="range"
              min="5"
              max="30"
              class="draw-range"
              @input="debouncedUpdate"
            >
          </label>
        </section>

        <!-- Importar y exportar -->
        <section class="io-section draw-section">
          <p class="draw-section-title">
            Importar / Exportar
          </p>

          <div class="io-controls">
            <input
              v-model="exportFileName"
              type="text"
              placeholder="Nombre del archivo"
              class="draw-text-input"
            >

            <div class="io-buttons">
              <button
                type="button"
                class="draw-io-btn draw-io-btn--secondary"
                title="Exportar a GeoJSON"
                @click="exportGeoJSON()"
              >
                <font-awesome-icon icon="download" />
                Exportar
              </button>

              <button
                type="button"
                class="draw-io-btn draw-io-btn--primary"
                title="Importar desde GeoJSON"
                @click="triggerImport()"
              >
                <font-awesome-icon icon="upload" />
                Importar
              </button>

              <input
                ref="fileInput"
                type="file"
                class="draw-file-input"
                accept=".geojson,application/json"
                @change="onFileChange"
              >
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { draggableDiv } from '@/components/mixin/draggableDiv.js'
import { vuex } from '@/components/mixin/vuex.js'
import { draw } from './draw.js'
import {
  DRAW_STYLE_CONTROLS,
  createDrawStyleConfigs,
  drawStyle,
  getStyleFromConfig,
  normalizeDrawStyleConfig
} from './drawStyle.js'
import { mapService } from '@/services/map/mapService.js'
import GeoJSON from 'ol/format/GeoJSON.js'
import { fromCircle } from 'ol/geom/Polygon.js'
import { createEmpty, extend as extendExtent, getWidth, isEmpty as isEmptyExtent } from 'ol/extent.js'
import DrawDialog from './DrawDialog.vue'
import {
  calculateShapeMeasurement,
  measurementItems
} from './drawMeasurements.js'

const svgCursor = (svg, hotspot = '4 20') =>
  `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotspot}, crosshair`

const lineCursor = svgCursor(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">'
  + '<path d="M4 20L20 4" fill="none" stroke="#102a43" stroke-width="3"/>'
  + '<circle cx="4" cy="20" r="2.5" fill="#f58220" stroke="white"/>'
  + '<circle cx="20" cy="4" r="2.5" fill="#f58220" stroke="white"/>'
  + '</svg>'
)

const ellipseCursor = svgCursor(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">'
  + '<ellipse cx="12" cy="12" rx="9" ry="6" fill="white" stroke="#102a43" stroke-width="2"/>'
  + '<circle cx="4" cy="18" r="2" fill="#f58220" stroke="white"/>'
  + '</svg>',
  '4 18'
)

const eraserCursor = svgCursor(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">'
  + '<path d="M5 17L14 5L21 11L12 21H7Z" fill="#f58220" stroke="#102a43" stroke-width="2"/>'
  + '<path d="M5 17L12 21" stroke="white" stroke-width="2"/>'
  + '</svg>',
  '6 18'
)

const DRAWING_CURSORS = Object.freeze({
  Point: 'crosshair',
  LineString: lineCursor,
  Polygon: lineCursor,
  Arrow: lineCursor,
  Freehand: lineCursor,
  Circle: ellipseCursor,
  Ellipse: ellipseCursor,
  Text: 'text',
  Eraser: eraserCursor,
  Selector: 'pointer'
})

const createDrawDialogState = () => ({
  kind: 'message',
  title: '',
  message: '',
  tone: 'info',
  inputVisible: false,
  inputLabel: 'Texto',
  placeholder: '',
  showCancel: false,
  confirmLabel: 'Aceptar',
  cancelLabel: 'Cancelar'
})

export default {
  name: 'draw',
  components: {
    DrawDialog
  },
  mixins: [vuex, draggableDiv, draw, drawStyle],
  props: ['isFixed', 'currentMode'],
  data() {
    return {
      map: null,
      isHidden: false,
      activeMode: null,
      currentDrawingType: null,
      suspendedDrawMode: null,
      lastEditableFeature: null,
      selectedFeature: null,
      drawingMeasurement: null,
      debounceTimer: null,
      exportFileName: '',
      drawDialog: createDrawDialogState(),
      drawDialogInput: '',
      drawDialogError: '',
      drawDialogOpen: false,
      pendingTextFeature: null,

      drawStyleConfig: {},
      configs: createDrawStyleConfigs(),

      toolNames: {
        'Point': 'Punto',
        'LineString': 'Línea',
        'Polygon': 'Polígono',
        'Circle': 'Círculo',
        'Arrow': 'Flecha',
        'Ellipse': 'Elipse',
        'Freehand': 'Mano Alzada',
        'Text': 'Texto',
        'Eraser': 'Goma',
        'Selector': 'Selector'
      }
    }
  },
  methods: {
    debouncedUpdate() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      const drawingType = this.currentDrawingType;
      const mode = this.activeMode;
      this.debounceTimer = setTimeout(() => {
        if (
          drawingType
          && drawingType === this.currentDrawingType
          && mode === this.activeMode
        ) {
          if (mode === 'select') {
            this.updateSelectedFeatureStyle(drawingType);
            return;
          }

          this.configs[drawingType] = { ...this.drawStyleConfig };
          this.updateDrawStyle(drawingType);
          this.updateLastEditableFeatureStyle(drawingType);
        }
      }, 100);
    },
    initDraw: function (type) {
      if (this.activeMode === 'draw' && this.currentDrawingType === type) {
        this.deactivateDrawingMode();
        return;
      }

      this.deactivateDrawingMode();

      // El @mousedown.capture del panel no dispara con Enter/Espacio en el botón.
      this.claimMapInteraction('draw');

      if (this.configs[type]) {
        this.drawStyleConfig = { ...this.configs[type] };
      }
      this.currentDrawingType = type;
      this.activeMode = 'draw';

      this.initDrawLayer();
      this.updateDrawStyle(type);
      this.initDrawInteraction(type);
      this.setDrawingCursor(type);
    },
    activateEraser: function () {
      if (this.activeMode === 'erase') {
        this.deactivateDrawingMode();
        return;
      }

      this.deactivateDrawingMode();
      this.claimMapInteraction('draw');
      this.initDrawLayer();
      this.activeMode = 'erase';
      this.initEraseInteraction();
      this.setDrawingCursor('Eraser');
    },
    activateSelector: function () {
      if (this.activeMode === 'select') {
        this.deactivateDrawingMode();
        return;
      }

      this.deactivateDrawingMode();
      this.claimMapInteraction('draw');
      this.initDrawLayer();
      this.activeMode = 'select';
      this.initSelectInteraction();
      this.setDrawingCursor('Selector');
    },
    isDrawingTypeActive: function (type) {
      return this.activeMode === 'draw' && this.currentDrawingType === type
    },
    setDrawingCursor: function (type) {
      document.body.style.cursor = '';
      const viewport = this.map && this.map.getViewport
        ? this.map.getViewport()
        : null;
      if (viewport) {
        viewport.style.cursor = type ? DRAWING_CURSORS[type] || 'crosshair' : '';
      }
    },
    deactivateDrawingMode: function (keepCursor) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      if (
        this.activeMode === 'draw'
        && this.currentDrawingType
        && this.configs[this.currentDrawingType]
      ) {
        this.configs[this.currentDrawingType] = { ...this.drawStyleConfig };
      }
      if (this.drawInteraction) {
        this.drawInteraction.abortDrawing();
        this.map.removeInteraction(this.drawInteraction);
        this.drawInteraction = null;
      }
      this.removeEraseInteraction();
      this.removeSelectInteraction();
      this.removeHelpTooltip();
      this.lockLastEditableFeature();
      this.clearSelectedFeature();
      this.currentDrawingType = null;
      this.activeMode = null;
      if (!keepCursor) this.setDrawingCursor(null);
    },
    suspendDrawingMode: function () {
      if (!this.activeMode) return;
      // deactivateDrawingMode() deja activeMode/currentDrawingType en null,
      // por eso el modo se guarda antes de llamarlo. keepCursor=true: el
      // cursor pasa a la herramienta que toma la prioridad (orden de watchers
      // entre componentes hace que Select ya lo haya puesto antes de llegar acá).
      this.suspendedDrawMode = { mode: this.activeMode, type: this.currentDrawingType };
      this.deactivateDrawingMode(true);
    },
    restoreDrawingMode: function () {
      const suspended = this.suspendedDrawMode;
      if (!suspended) return;
      this.suspendedDrawMode = null;
      if (suspended.mode === 'erase') this.activateEraser();
      else if (suspended.mode === 'select') this.activateSelector();
      else if (suspended.type) this.initDraw(suspended.type);
    },
    lockLastEditableFeature: function () {
      this.lastEditableFeature = null;
    },
    updateDrawingMeasurement: function (feature, drawingType) {
      this.drawingMeasurement = calculateShapeMeasurement(
        feature,
        drawingType
      );
    },
    clearDrawingMeasurement: function () {
      this.drawingMeasurement = null;
    },
    updateLastEditableFeatureStyle: function (drawingType) {
      const feature = this.lastEditableFeature;
      if (!feature || feature.get('drawingType') !== drawingType) return;

      const config = { ...this.drawStyleConfig };
      feature.set('drawStyleConfig', config);
      feature.setStyle(getStyleFromConfig(
        config,
        feature.get('text') || '',
        drawingType
      ));
      feature.changed();
    },
    inferDrawingType: function (feature) {
      const storedType = feature.get('drawingType');
      if (DRAW_STYLE_CONTROLS[storedType]) return storedType;

      const geometry = feature.getGeometry();
      const geometryType = geometry ? geometry.getType() : '';
      if (geometryType === 'Point' || geometryType === 'MultiPoint') {
        return feature.get('text') ? 'Text' : 'Point';
      }
      if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
        return 'LineString';
      }
      if (geometryType === 'Circle') return 'Circle';
      return 'Polygon';
    },
    onFeatureSelected: function (feature) {
      if (this.activeMode !== 'select' || !feature) return;

      const drawingType = this.inferDrawingType(feature);
      const config = normalizeDrawStyleConfig(
        feature.get('drawStyleConfig') || {},
        drawingType
      );

      this.selectedFeature = feature;
      this.currentDrawingType = drawingType;
      this.drawStyleConfig = { ...config };
      feature.set('drawingType', drawingType);
      feature.set('drawStyleConfig', { ...config });
      this.refreshSelectionHighlight(feature);
    },
    clearSelectedFeature: function () {
      this.selectedFeature = null;
      this.clearSelectionHighlight();
      if (this.activeMode === 'select') {
        this.currentDrawingType = null;
      }
    },
    updateSelectedFeatureStyle: function (drawingType) {
      const feature = this.selectedFeature;
      if (
        this.activeMode !== 'select'
        || !feature
        || feature.get('drawingType') !== drawingType
      ) return;

      const config = normalizeDrawStyleConfig(
        this.drawStyleConfig,
        drawingType
      );
      this.drawStyleConfig = { ...config };
      feature.set('drawStyleConfig', { ...config });
      feature.setStyle(getStyleFromConfig(
        config,
        feature.get('text') || '',
        drawingType
      ));
      feature.changed();
      this.refreshSelectionHighlight(feature);
    },
    onFeatureErased: function (feature) {
      if (this.lastEditableFeature === feature) {
        this.lockLastEditableFeature();
      }
    },
    openDrawDialog: function (options, inputValue = '') {
      this.drawDialog = {
        ...createDrawDialogState(),
        ...options
      };
      this.drawDialogInput = inputValue;
      this.drawDialogError = '';
      this.drawDialogOpen = true;
      this.$nextTick(() => {
        this.$modal.show('draw-dialog');
      });
    },
    resetDrawDialogState: function () {
      this.drawDialog = createDrawDialogState();
      this.drawDialogInput = '';
      this.drawDialogError = '';
    },
    cancelPendingTextFeature: function () {
      if (!this.pendingTextFeature) return;
      if (this.drawSource) {
        this.drawSource.removeFeature(this.pendingTextFeature);
      }
      this.pendingTextFeature = null;
    },
    requestTextForFeature: function (feature) {
      this.pendingTextFeature = feature;
      this.openDrawDialog({
        kind: 'text',
        title: 'Agregar texto al mapa',
        message: 'Escriba el contenido que desea ubicar en el punto seleccionado.',
        tone: 'info',
        inputVisible: true,
        inputLabel: 'Contenido del texto',
        placeholder: 'Escriba un texto',
        showCancel: true,
        confirmLabel: 'Agregar',
        cancelLabel: 'Cancelar'
      });
    },
    requestClearAllDrawings: function () {
      const hasDrawings = this.drawSource
        && this.drawSource.getFeatures().length > 0;
      if (!hasDrawings) {
        this.showDrawMessage(
          'No hay dibujos',
          'Todavía no existen elementos para eliminar.',
          'info'
        );
        return;
      }

      this.openDrawDialog({
        kind: 'clear',
        title: 'Eliminar todos los dibujos',
        message: 'Esta acción eliminará todos los elementos dibujados en el mapa.',
        tone: 'danger',
        showCancel: true,
        confirmLabel: 'Eliminar todo',
        cancelLabel: 'Cancelar'
      });
    },
    showDrawMessage: function (title, message, tone = 'info') {
      this.openDrawDialog({
        kind: 'message',
        title,
        message,
        tone,
        showCancel: false,
        confirmLabel: 'Entendido'
      });
    },
    confirmDrawDialog: function () {
      const kind = this.drawDialog.kind;
      if (kind === 'text') {
        const textContent = this.drawDialogInput.trim();
        if (!textContent) {
          this.drawDialogError = 'Debe ingresar un texto antes de continuar.';
          return;
        }

        const feature = this.pendingTextFeature;
        this.pendingTextFeature = null;
        if (feature) {
          feature.set('text', textContent);
          this.finalizeDrawnFeature(feature, textContent);
        }
      } else if (kind === 'clear') {
        this.clearAllDrawings();
      }

      this.drawDialogOpen = false;
      this.$modal.hide('draw-dialog');
    },
    cancelDrawDialog: function () {
      this.cancelPendingTextFeature();
      this.drawDialogOpen = false;
      this.$modal.hide('draw-dialog');
    },
    onDrawDialogClosed: function () {
      if (this.drawDialogOpen) {
        this.cancelPendingTextFeature();
      }
      this.drawDialogOpen = false;
      this.resetDrawDialogState();
    },
    exitDraw: function () {
      if (this.drawDialogOpen) {
        this.cancelDrawDialog();
      }
      this.deactivateDrawingMode();
      if (this.drawLayer) {
        this.drawLayer.setVisible(false);
      }
      this.selectMapTool('draw', false)
    },
    // Al desplazarse en horizontal el mapa se repite, y una figura dibujada en
    // la segunda vuelta queda con coordenadas fuera del mundo real. El renderer
    // arma el replay group sólo con lo que cae en el mundo real
    // (ol/renderer/canvas/VectorLayer.js:431), así que esa figura no se dibujaba
    // en ninguna parte. Corriéndola un ancho de mundo entero queda en el mundo
    // real y el mismo renderer la repite en todas las copias: se ve donde la
    // dibujaste y también al dar la vuelta.
    wrapGeometryToWorld: function (geometry) {
      const projection = this.map && this.map.getView().getProjection();
      if (!geometry || !projection || !projection.canWrapX()) return;
      const projectionExtent = projection.getExtent();
      const worldWidth = getWidth(projectionExtent);
      const extent = geometry.getExtent();
      const centerX = (extent[0] + extent[2]) / 2;
      const worlds = Math.floor((centerX - projectionExtent[0]) / worldWidth);
      if (worlds !== 0) geometry.translate(-worlds * worldWidth, 0);
    },
    finalizeDrawnFeature: function (feature, textContent = '') {
      this.wrapGeometryToWorld(feature.getGeometry());
      feature.set('drawStyleConfig', { ...this.drawStyleConfig });
      feature.set('drawingType', this.currentDrawingType);

      feature.setStyle(getStyleFromConfig(
        this.drawStyleConfig,
        textContent,
        this.currentDrawingType
      ));

      this.pushFeature(feature);
      this.lastEditableFeature = feature;
    },
    onDrawEnd(event){
      if (this.currentDrawingType === 'Text') {
        this.requestTextForFeature(event.feature);
        return;
      }

      this.finalizeDrawnFeature(event.feature);
    },
    exportGeoJSON() {
      const features = this.drawSource
        ? this.drawSource.getFeatures()
        : [];
      if (features.length === 0) {
        this.showDrawMessage(
          'No hay dibujos para exportar',
          'Agregue al menos un elemento antes de generar el archivo GeoJSON.',
          'warning'
        );
        return;
      }

      try {
        const format = new GeoJSON({
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
        // OpenLayers no sabe escribir un Circle en GeoJSON y NO lanza error: lo
        // emite como {"type":"GeometryCollection","geometries":[]}, una cáscara
        // vacía, y el círculo se pierde entero al reimportar (verificado en los
        // archivos exportados). Se convierte a polígono antes de escribir, sobre
        // un clon para no tocar lo que está en el mapa. Conserva drawingType
        // 'Circle', así inferDrawingType lo reconoce al volver y el editor sigue
        // ofreciendo sus controles de relleno y contorno.
        // ponytail: al reimportar es un polígono y ya no se reescala como
        // círculo. Si algún día hace falta, guardar centro y radio como
        // propiedades y reconstruir la geometría en la importación.
        const exportable = features.map(feature => {
          const geometry = feature.getGeometry();
          if (!geometry || geometry.getType() !== 'Circle') return feature;
          const asPolygon = feature.clone();
          asPolygon.setGeometry(fromCircle(geometry, 64));
          return asPolygon;
        });
        const json = format.writeFeatures(exportable);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        let name = this.exportFileName.trim();
        if (!name) {
          const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          name = `dibujos_${date}`;
        }

        link.href = url;
        link.download = `${name}.geojson`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al exportar GeoJSON:", error);
        this.showDrawMessage(
          'No fue posible exportar los dibujos',
          'Revise los elementos dibujados e intente nuevamente.',
          'danger'
        );
      }
    },
    triggerImport() {
      this.$refs.fileInput.click();
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.importGeoJSON(e.target.result);
      };
      reader.onerror = () => {
        this.showDrawMessage(
          'No fue posible leer el archivo',
          'Seleccione nuevamente el archivo e intente otra vez.',
          'danger'
        );
      };
      reader.readAsText(file);
      event.target.value = ''; // Reset for same file re-import
    },
    importGeoJSON(json) {
      try {
        this.lockLastEditableFeature();
        this.clearSelectedFeature();
        const format = new GeoJSON({
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
        const features = format.readFeatures(json);
        if (features.length === 0) {
          this.showDrawMessage(
            'Archivo sin dibujos',
            'El archivo seleccionado no contiene elementos geográficos válidos.',
            'warning'
          );
          return;
        }

        this.initDrawLayer(); // Ensure layer exists

        features.forEach(feature => {
          const text = feature.get('text');
          const drawingType = this.inferDrawingType(feature);
          const config = normalizeDrawStyleConfig(
            feature.get('drawStyleConfig') || {},
            drawingType
          );

          feature.set('drawingType', drawingType);
          feature.set('drawStyleConfig', { ...config });
          feature.setStyle(getStyleFromConfig(config, text, drawingType));
          
          this.drawSource.addFeature(feature);
          this.pushFeature(feature);
        });

        // Zoom a lo recién importado. Sin esto los dibujos podían caer fuera de
        // la vista actual y la importación parecía no haber hecho nada. Sólo se
        // mide lo importado, no lo que ya estaba dibujado. Una geometría vacía
        // (los círculos exportados por la versión anterior) devuelve una
        // extensión vacía y extend() la ignora, así que no descuadra el encuadre.
        const extent = createEmpty();
        features.forEach(feature => {
          const geometry = feature.getGeometry();
          if (geometry) extendExtent(extent, geometry.getExtent());
        });
        if (!isEmptyExtent(extent)) {
          //maxZoom: un solo punto importado dejaría la vista pegada al suelo.
          this.map.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 18, duration: 300 });
        }
      } catch (e) {
        console.error("Error al importar GeoJSON:", e);
        this.showDrawMessage(
          'No fue posible importar el archivo',
          'Verifique que sea un archivo GeoJSON válido e intente nuevamente.',
          'danger'
        );
      }
    }
  },
  computed: {
    isActive: function () {
      return this.mapToolValues('draw').active
    },
    styleControls: function () {
      return DRAW_STYLE_CONTROLS[this.currentDrawingType] || {}
    },
    hasStyleControls: function () {
      return Object.keys(this.styleControls).length > 0
    },
    lastEditableDrawingType: function () {
      return this.lastEditableFeature
        ? this.lastEditableFeature.get('drawingType')
        : null
    },
    selectedDrawingType: function () {
      return this.selectedFeature
        ? this.selectedFeature.get('drawingType')
        : null
    },
    activeShapeMeasurement: function () {
      if (this.drawingMeasurement) return this.drawingMeasurement

      const feature = this.selectedFeature || this.lastEditableFeature
      if (!feature) return null

      return calculateShapeMeasurement(
        feature,
        feature.get('drawingType')
      )
    },
    shapeMeasurementItems: function () {
      return measurementItems(this.activeShapeMeasurement)
    },
    shapeMeasurementContext: function () {
      if (this.drawingMeasurement) return 'Mientras dibuja'
      if (this.selectedFeature) return 'Figura seleccionada'
      return 'Última figura dibujada'
    },
    activeModeLabel: function () {
      if (this.activeMode === 'erase') return this.toolNames.Eraser
      if (this.activeMode === 'select') return this.toolNames.Selector
      return this.toolNames[this.currentDrawingType] || ''
    }
  },
  watch: {
    isActive: function (active) {
      if (!active) {
        this.suspendedDrawMode = null;
        this.deactivateDrawingMode();
      }
    },
    // Perder la prioridad suspende la interacción sin cerrar el panel;
    // recuperarla re-arma el mismo modo (DoD 2).
    mapInteractionTool: function (tool) {
      if (tool === 'draw') {
        this.restoreDrawingMode();
        return;
      }
      this.suspendDrawingMode();
    }
  },
  mounted: function () {
    this.makeDraggable("drawID");
    this.map = mapService.get();
    // exitDraw() deja la capa oculta al cerrar la herramienta y sólo
    // initDrawLayer() vuelve a mostrarla, pero nadie la llamaba al abrir: los
    // dibujos seguían invisibles hasta que el usuario elegía una forma. Engancharla
    // acá hace que abrir la herramienta muestre de inmediato lo ya dibujado.
    if (this.map) this.initDrawLayer();
  },
  beforeDestroy: function () {
    this.exitDraw()
    this.destroySelectionLayer()
  }
}
</script>

<style scoped lang="scss">
.draw-tool-window {
  top: 4em;
  right: 16px;

  width: 360px;
  max-width: calc(100vw - 32px);

  z-index: 1000;
}

.draw-tool-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}


.draw-current-mode {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 9px 11px;

  background: var(--color-mist);
  border-left: 3px solid var(--color-orange);
  border-radius: 4px;

  font-size: 0.8rem;

  span {
    color: var(--color-ghost);
  }

  strong {
    color: var(--color-navy);
    font-weight: 700;
  }
}

.draw-editing-status {
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 9px 11px;

  background: #fff8ef;
  border: 1px solid #f4c58f;
  border-radius: 4px;

  color: var(--color-navy);
  font-size: 0.78rem;

  svg {
    color: var(--color-orange);
  }

  strong {
    font-weight: 700;
  }
}

.draw-editing-status--selected {
  background: #eef7ff;
  border-color: #9bc9ec;

  svg {
    color: var(--color-steel);
  }
}

.draw-measurement-card {
  display: flex;
  flex-direction: column;
  gap: 9px;

  padding: 11px;

  background: #f4f7fa;
  border: 1px solid var(--color-line);
  border-left: 3px solid var(--color-steel);
  border-radius: 4px;

  color: var(--color-navy);

  small {
    color: var(--color-ghost);
    font-size: 0.7rem;
    line-height: 1.35;
  }
}

.draw-measurement-card__header {
  display: flex;
  align-items: center;
  gap: 9px;

  svg {
    color: var(--color-orange);
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  strong {
    font-size: 0.8rem;
    font-weight: 700;
  }

  span {
    color: var(--color-ghost);
    font-size: 0.7rem;
  }
}

.draw-measurement-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;

  margin: 0;

  div {
    min-width: 0;
    padding: 7px 6px;

    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: 4px;

    text-align: center;
  }

  dt {
    color: var(--color-ghost);
    font-size: 0.66rem;
    line-height: 1.2;
  }

  dd {
    margin: 3px 0 0;

    color: var(--color-navy);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
}


.draw-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.draw-section + .draw-section {
  padding-top: 14px;
  border-top: 1px solid var(--color-line);
}

.draw-section-title {
  margin: 0;

  color: var(--color-navy);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.3;
}


.draw-buttons-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.draw-tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 0;
  height: 40px;
  padding: 8px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  color: var(--color-navy);
  font-size: 0.95rem;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: var(--color-mist);
    border-color: var(--color-steel);
    color: var(--color-steel);
  }

  &:focus-visible {
    outline: 2px solid var(--color-steel);
    outline-offset: 2px;
  }
}

.draw-tool-btn.active-tool {
  background: var(--color-orange);
  border-color: var(--color-orange);
  color: var(--color-white);
}

.draw-ellipse-icon {
  display: inline-block;

  width: 20px;
  height: 12px;

  border: 2px solid currentColor;
  border-radius: 50%;
}

.draw-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.draw-history-btn,
.draw-clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  min-height: 36px;
  padding: 8px 12px;

  border-radius: 4px;

  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.draw-history-btn {
  width: 42px;

  background: var(--color-white);
  border: 1px solid var(--color-line);

  color: var(--color-navy);

  &:hover:not(:disabled) {
    background: var(--color-mist);
    border-color: var(--color-steel);
  }

  &:disabled {
    background: var(--color-mist);
    border-color: var(--color-line);
    color: var(--color-ghost);

    cursor: not-allowed;
  }
}

.draw-clear-btn {
  flex: 1;

  background: var(--color-white);
  border: 1px solid var(--color-danger);

  color: var(--color-danger);

  &:hover {
    background: var(--color-danger);
    color: var(--color-white);
  }
}

.interactionConfig {
  padding: 12px;

  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;

    margin: 0;

    color: var(--color-navy);
    font-size: 0.78rem;
    font-weight: 600;
  }

  label + label {
    margin-top: 2px;
  }
}

.draw-select,
.draw-text-input {
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;

  padding: 7px 10px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  color: var(--color-graphite);
  font-family: inherit;
  font-size: 0.82rem;

  &:hover {
    border-color: var(--color-steel);
  }

  &:focus {
    border-color: var(--color-steel);
    outline: 2px solid var(--color-fog);
    outline-offset: 1px;
  }

  &::placeholder {
    color: var(--color-ghost);
  }
}

/* Selector de color */

.draw-color-input {
  width: 100%;
  height: 36px;
  padding: 3px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  cursor: pointer;

  &:hover {
    border-color: var(--color-steel);
  }
}

.draw-range {
  width: 100%;
  height: 6px;

  appearance: none;
  -webkit-appearance: none;

  background: var(--color-fog);
  border-radius: 4px;

  cursor: pointer;
}

.draw-range::-webkit-slider-thumb {
  width: 16px;
  height: 16px;

  appearance: none;
  -webkit-appearance: none;

  background: var(--color-orange);
  border: 2px solid var(--color-white);
  border-radius: 50%;

  cursor: pointer;
}

.draw-range::-moz-range-thumb {
  width: 16px;
  height: 16px;

  background: var(--color-orange);
  border: 2px solid var(--color-white);
  border-radius: 50%;

  cursor: pointer;
}


.io-section {
  margin: 0;
}

.io-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.io-buttons {
  display: flex;
  gap: 8px;
}

.draw-io-btn {
  flex: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  min-height: 36px;
  padding: 8px 12px;

  border-radius: 4px;

  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.draw-io-btn--secondary {
  background: var(--color-white);
  border: 1px solid var(--color-line);

  color: var(--color-graphite);

  &:hover {
    background: var(--color-mist);
    border-color: var(--color-steel);
    color: var(--color-navy);
  }
}

.draw-io-btn--primary {
  background: var(--color-navy);
  border: 1px solid var(--color-navy);

  color: var(--color-white);

  &:hover {
    background: var(--color-steel);
    border-color: var(--color-steel);
  }
}

.draw-file-input {
  display: none;
}

.draw-history-btn:focus-visible,
.draw-clear-btn:focus-visible,
.draw-io-btn:focus-visible,
.draw-color-input:focus-visible,
.draw-range:focus-visible {
  outline: 2px solid var(--color-steel);
  outline-offset: 2px;
}
</style>
